import { memo, useEffect, useState, FC, ChangeEvent, useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useTenantJob } from "../../../hooks/useTenantJob";
import { useMessage } from "../../../hooks/useMessage";
import { ScheduleSettings } from "../../../hooks/useParameters";
import { JobMetricDrawer } from "../../molecules/JobMetricDrawer";
import { StepFunctionMetricDrawer } from "../../molecules/StepFunctionMetricDrawer";
import { useEventBridge } from "../../../hooks/useEventBridge";

type Props = {
  TenantJob: ScheduleSettings | undefined;
  isOpen: boolean;
  onClose: () => void;
  isEditable?: boolean;
  tenant_id: string;
};

export const TenantJobModal: FC<Props> = memo((props) => {
  const { isOpen, onClose, TenantJob, isEditable, tenant_id } = props;
  const { showMessage } = useMessage();
  const {
    getScheduleSettingDetail,
    updateRuleState,
    updateSchedule,
    updateRuleTarget,
    putParameter,
    loading,
    loadingDescribeRule,
    describeRule,
    setParameters,
    parameters,
    ruleTargets
  } = useTenantJob(onClose);
  const { executeStateMachine, loading: executingJob } = useEventBridge();
  const [isUpdating, setIsUpdating] = useState<number | null>(null);
  const [executionInput, setExecutionInput] = useState<string>("");
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [draftSchedule, setDraftSchedule] = useState("");
  const toast = useToast();
  
  useEffect(() => {
    onModalReset();
  }, [TenantJob]);

  const onModalReset = () => {
    if (TenantJob) {
      getScheduleSettingDetail(TenantJob);
      if (TenantJob.inputTransform) {
        setExecutionInput(JSON.stringify(TenantJob.inputTransform, null, 2));
      } else {
        setExecutionInput("");
      }
    }
  };
  
  useEffect(() => {
    if (ruleTargets.length === 0 || !TenantJob) return;
    
    const firstTarget = ruleTargets[0];
    
    if (firstTarget?.InputTransformer?.InputTemplate) {
      try {
        const parsedTemplate = JSON.parse(firstTarget.InputTransformer.InputTemplate);
        setExecutionInput(JSON.stringify(parsedTemplate, null, 2));
      } catch (e) {
        if (TenantJob.inputTransform) {
          setExecutionInput(JSON.stringify(TenantJob.inputTransform, null, 2));
        }
      }
    } else if (firstTarget?.Input) {
      try {
        const parsedInput = JSON.parse(firstTarget.Input);
        setExecutionInput(JSON.stringify(parsedInput, null, 2));
      } catch (e) {
        setExecutionInput(firstTarget.Input);
      }
    }
  }, [ruleTargets, TenantJob]);
  
  const handleParamChange = (index: number, value: string) => {
    // Add implementation for parameter change
    // This would typically update a local state that holds modified parameters
  };
  
  const onChangeTextPart = (index: number, e: ChangeEvent<HTMLTextAreaElement>) => {
    // 新しい配列を作成して変更を適用する
    const updatedParameters = parameters.map((param, i) => {
      if (i === index) {
        // 変更対象のパラメータの場合は新しいオブジェクトを作成して値を更新
        return { ...param, value: e.target.value };
      }
      // それ以外のパラメータはそのまま返す
      return param;
    });
    // 新しい配列でステートを更新
    setParameters(updatedParameters);
  };
  
  const handleUpdateParam = (index: number) => {
    if (!parameters[index] || parameters[index].path === undefined) return;
    
    setIsUpdating(index);
    const param = parameters[index];
    
    // パラメータ更新の実装
    putParameter(param.path, param.value || "");
    
    // 少し遅延を入れて更新中の状態を表示
    setTimeout(() => {
      setIsUpdating(null);
      // 画面に表示するメッセージ (putParameter内でもメッセージは表示されます)
      showMessage({
        title: `${param.name}の更新処理を実行しました`,
        status: "info",
      });
    }, 1000);
  };

  const executeJob = async () => {
    if (!TenantJob?.stateMachineArn) {
      toast({
        title: "エラー",
        description: "Step Function ARNが見つかりません",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      let input: any;
      
      if (!executionInput || executionInput.trim() === '') {
        input = {};
      } else {
        const parsed = JSON.parse(executionInput);
        input = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      }
      
      console.log('Executing Step Function with input:', input);
      await executeStateMachine(TenantJob.stateMachineArn, input);
      
      toast({
        title: "実行開始",
        description: `${TenantJob.name} の実行を開始しました`,
        status: "success",
        duration: 3000,
      });
    } catch (error: any) {
      console.error('Execute job error:', error);
      toast({
        title: "実行エラー",
        description: error.response?.data?.message || "パラメータの形式が正しくないか、実行に失敗しました",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onModalReset();
        onClose();
      }}
      motionPreset="slideInBottom"
      autoFocus={false}
      size={"4xl"}
    >
      <ModalOverlay />
      <ModalContent pb={2}>
        <ModalHeader>処理設定</ModalHeader>
        <ModalCloseButton />
        <ModalBody mx={{ base: 0, md: 6 }}>
            <Tabs>
              <TabList>
                <Tab>EventBridge</Tab>
                <Tab>parameter</Tab>
                <Tab>処理件数</Tab>
                {TenantJob?.stateMachineArn && <Tab>メトリック</Tab>}
                <Tab>詳細</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  {TenantJob?.eventsRuleName === "manual" ? (
                    <Stack spacing={4}>
                      <Badge colorScheme="orange" fontSize="md" p={2}>手動実行専用ジョブ（EventBridgeルールなし）</Badge>
                      {isEditable && TenantJob?.stateMachineArn && (
                        <>
                          <FormControl>
                            <FormLabel>実行パラメータ</FormLabel>
                            <Textarea
                              value={executionInput}
                              onChange={(e) => setExecutionInput(e.target.value)}
                              minHeight="200px"
                              border="1px"
                              borderColor={"gray.400"}
                              placeholder="JSON形式で実行パラメータを入力してください"
                            />
                          </FormControl>
                          <HStack>
                            <Button
                              colorScheme="blue"
                              onClick={executeJob}
                              isLoading={executingJob}
                              disabled={executingJob}
                            >
                              実行
                            </Button>
                          </HStack>
                        </>
                      )}
                    </Stack>
                  ) : (
                    <Stack spacing={4}>
                      <FormControl>
                        <FormLabel>Name</FormLabel>
                        <Input
                          readOnly={true}
                          value={describeRule?.Name}
                          placeholder="Name"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Description</FormLabel>
                        <Input
                          readOnly={true}
                          value={describeRule?.Description}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>ScheduleExpression</FormLabel>
                        {editingSchedule ? (
                          <Stack spacing={2}>
                            <Input
                              value={draftSchedule}
                              onChange={(e) => setDraftSchedule(e.target.value)}
                              placeholder="cron(0 15 * * ? *)"
                              fontFamily="mono"
                            />
                            <Text fontSize="xs" color="gray.500">
                              形式: cron(分 時 日 月 曜日 年) — 時刻はUTC（JST-9時間）
                            </Text>
                            <HStack spacing={2} flexWrap="wrap">
                              {[
                                { label: "毎分", value: "rate(1 minute)" },
                                { label: "毎時", value: "rate(1 hour)" },
                                { label: "毎日 00:20 JST", value: "cron(20 15 * * ? *)" },
                                { label: "毎日 09:00 JST", value: "cron(0 0 * * ? *)" },
                                { label: "毎月1日 00:11 JST", value: "cron(11 15 1 * ? *)" },
                              ].map((preset) => (
                                <Button
                                  key={preset.value}
                                  size="xs"
                                  variant={draftSchedule === preset.value ? "solid" : "outline"}
                                  colorScheme="teal"
                                  onClick={() => setDraftSchedule(preset.value)}
                                >
                                  {preset.label}
                                </Button>
                              ))}
                            </HStack>
                            <HStack>
                              <Button
                                size="sm"
                                colorScheme="teal"
                                onClick={() => {
                                  if (describeRule?.Name && draftSchedule) {
                                    updateSchedule(describeRule.Name, draftSchedule);
                                    setEditingSchedule(false);
                                  }
                                }}
                                isLoading={loadingDescribeRule}
                              >
                                保存
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setEditingSchedule(false);
                                  setDraftSchedule(describeRule?.ScheduleExpression || "");
                                }}
                              >
                                キャンセル
                              </Button>
                            </HStack>
                          </Stack>
                        ) : (
                          <Input
                            readOnly={true}
                            value={describeRule?.ScheduleExpression}
                            onDoubleClick={() => {
                              if (isEditable) {
                                setDraftSchedule(describeRule?.ScheduleExpression || "");
                                setEditingSchedule(true);
                              }
                            }}
                            cursor={isEditable ? "pointer" : "default"}
                          />
                        )}
                      </FormControl>
                      <FormControl>
                        <FormLabel>State</FormLabel>
                        <Stack direction="row" spacing={4} align="center">
                          <Input
                            readOnly={true}
                            value={describeRule?.State}
                            width="auto"
                            flexGrow={1}
                          />
                          {isEditable && describeRule?.Name && (
                            <Button
                              colorScheme={describeRule?.State === "ENABLED" ? "red" : "green"}
                              onClick={() => {
                                if (describeRule?.Name) {
                                  const newState = describeRule?.State === "ENABLED" ? "DISABLED" : "ENABLED";
                                  updateRuleState(describeRule.Name, newState);
                                }
                              }}
                              isLoading={loadingDescribeRule}
                            >
                              {describeRule?.State === "ENABLED" ? "無効化" : "有効化"}
                            </Button>
                          )}
                        </Stack>
                      </FormControl>
                      {isEditable && TenantJob?.stateMachineArn && (
                        <>
                          <FormControl>
                            <FormLabel>実行パラメータ</FormLabel>
                            <Textarea
                              value={executionInput}
                              onChange={(e) => setExecutionInput(e.target.value)}
                              minHeight="200px"
                              border="1px"
                              borderColor={"gray.400"}
                              placeholder="JSON形式で実行パラメータを入力してください"
                            />
                          </FormControl>
                          <HStack>
                            <Button
                              colorScheme="blue"
                              onClick={executeJob}
                              isLoading={executingJob}
                              disabled={executingJob}
                            >
                              実行
                            </Button>
                            <Button
                              colorScheme="teal"
                              variant="outline"
                              onClick={() => {
                                if (!describeRule?.Name || ruleTargets.length === 0) return;
                                try {
                                  const input = JSON.parse(executionInput);
                                  updateRuleTarget(describeRule.Name, ruleTargets[0].Id, input);
                                } catch {
                                  toast({ title: "JSONの形式が正しくありません", status: "error", duration: 3000 });
                                }
                              }}
                              disabled={!describeRule?.Name || ruleTargets.length === 0}
                            >
                              保存
                            </Button>
                          </HStack>
                        </>
                      )}
                    </Stack>
                  )}
                </TabPanel>
                <TabPanel>
                  <Stack>
                    {parameters && parameters.length > 0 ? (
                      parameters.map((param, index) => (
                        <FormControl key={index}>
                          <FormLabel>
                            {param.name}
                          </FormLabel>
                          {isEditable ? (
                            <>
                              <Textarea
                                value={param.value || ""}
                                onChange={(e) => onChangeTextPart(index, e)}
                                minHeight="100px"
                                size="sm"
                                border="1px"
                                borderColor={"gray.400"}
                              />
                              <Button 
                                mt={2} 
                                colorScheme="blue" 
                                size="sm" 
                                onClick={() => handleUpdateParam(index)}
                                isLoading={isUpdating === index}
                              >
                                更新
                              </Button>
                            </>
                          ) : (
                            <Textarea
                              readOnly={true}
                              value={param.value || ""}
                              minHeight="100px"
                              size="sm"
                              border="1px"
                              borderColor={"gray.400"}
                            />
                          )}
                        </FormControl>
                      ))
                    ) : (
                      <Center py={4}>
                        {loading ? (
                          <Spinner />
                        ) : (
                          <Badge colorScheme="gray">No parameters found</Badge>
                        )}
                      </Center>
                    )}
                  </Stack>
                </TabPanel>
                <TabPanel>
                  <Stack>
                    <JobMetricDrawer
                      tenant_id={tenant_id}
                      tenantJob={TenantJob}
                    ></JobMetricDrawer>
                  </Stack>
                </TabPanel>
                {TenantJob?.stateMachineArn && (
                  <TabPanel>
                    <Stack>
                      <StepFunctionMetricDrawer
                        stateMachineArn={TenantJob?.stateMachineArn}
                      />
                    </Stack>
                  </TabPanel>
                )}
                <TabPanel>
                  <Stack spacing={4}>
                    {TenantJob?.parampath && (() => {
                      const pathParts = TenantJob.parampath.split('/');
                      const jobKey = pathParts[pathParts.length - 1];
                      return (
                        <Box bg="blue.50" p={3} borderRadius="md">
                          <Text fontSize="sm" color="gray.600" mb={1}>JobKey (CloudWatch Dimension)</Text>
                          <Text fontSize="md" fontWeight="bold" color="blue.700">{jobKey}</Text>
                        </Box>
                      );
                    })()}
                    <Textarea
                      value={JSON.stringify(TenantJob, null, 2)}
                      readOnly={true}
                      rows={20}
                      border="1px"
                      borderColor={"gray.400"}
                    />
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
        </ModalBody>
        <ModalFooter></ModalFooter>
      </ModalContent>
    </Modal>
  );
});
